import { type DataTableColumn, type DataTableDefaultColumnProps } from 'mantine-datatable'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import type { CoinItemInGetList, CoinItemInPostList } from '@/services/coin/coins'
import type { CoinSlug } from '@/types/coin'

import styles from '../styles.module.css'
import ActionColumn from './Action'
import CoinColumn from './Coin'
import MarketCapColumn from './MarketCap'
import PriceColumn from './Price'
import PriceChangeColumn from './PriceChange'
import PriceChartColumn from './PriceChart'
import VolumeColumn from './Volume'

export type TableRecord = CoinItemInGetList | CoinItemInPostList

export const defaultColumnProps: DataTableDefaultColumnProps<TableRecord> = {
  titleClassName: `typography-body ${styles.tableHeader}`,
  cellsClassName: `typography-body-number ${styles.tableCell}`,
}

export function useColumns(
  restrictedAreasMap: Map<CoinSlug, string[]>,
  isOGPrice?: boolean,
  coinColumnTitle?: string,
  isStockTokens?: boolean,
) {
  const t = useTranslations('coinTable.headers')
  return useMemo<DataTableColumn<TableRecord>[]>(
    () => [
      {
        accessor: 'rank',
        title: '#',
        sortKey: 'rank',
        sortable: true,
        textAlign: 'left',
        visibleMediaQuery: theme => `(min-width: ${theme.breakpoints.midScale})`,
      },
      {
        accessor: 'coin',
        title: coinColumnTitle ?? t('coin'),
        sortable: true,
        sortKey: 'name',
        ellipsis: true,
        cellsClassName: styles.defaultCell,
        render: coin => (
          <CoinColumn coin={coin} isOGPrice={isOGPrice} isStockTokens={isStockTokens} />
        ),
      },
      {
        accessor: 'price',
        title: t('price'),
        sortable: true,
        sortKey: 'usd_price',
        textAlign: 'right',
        render: coin => <PriceColumn coin={coin} />,
      },
      {
        accessor: '24hChange',
        title: t('24hChange'),
        width: '120px',
        textAlign: 'right',
        sortable: true,
        sortKey: 'usd_price_change_24h',
        render: coin => <PriceChangeColumn coin={coin} />,
      },
      {
        accessor: '24hChart',
        title: t('24HChart'),
        textAlign: 'right',
        cellsClassName: styles.defaultCell,
        render: coin => <PriceChartColumn coinId={coin.token_id} />,
      },
      {
        accessor: 'marketCap',
        title: t('marketCap'),
        textAlign: 'right',
        sortable: true,
        sortKey: 'usd_marketcap',
        render: coin => <MarketCapColumn coin={coin} />,
      },
      {
        accessor: 'volume',
        title: t('24hVolume'),
        textAlign: 'right',
        sortable: true,
        sortKey: 'usd_volume_24h',
        render: coin => <VolumeColumn coin={coin} />,
      },
      {
        accessor: 'action',
        title: '',
        textAlign: 'center',
        // Constrain the actions column to be no wider than its content
        width: '0%',
        cellsClassName: styles.defaultCell,
        render: coin => (
          <ActionColumn
            isOGPrice={isOGPrice}
            restrictedAreas={restrictedAreasMap.get(coin.slug) ?? []}
          />
        ),
      },
    ],
    [t, restrictedAreasMap, isOGPrice, coinColumnTitle, isStockTokens],
  )
}
